import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useEffect, useMemo, useState } from "react";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import Societe from "../../services/Societe";
import SellingService from "../../services/SellingService";
import DevisNonConfirme from "./Table/DevisNonConfirme";
import DevisArchived from "./Table/DevisArchived";
import withLoader from "../../components/layouts/WithLoader";

const animatedComponents = makeAnimated();
const ALL_CLIENTS = { value: 0, label: "Tous" };
const CREATE_ROLES = ["ADMIN", "USER_B2B", "PURCHASING_MANAGER", "COMMERCIAL"];
const fmt = (v) => parseFloat(Number(v).toFixed(3));

const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };
const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};

const KpiCard = ({ label, value, icon, color, cols = "col-md-3 col-sm-6" }) => (
  <div className={cols}>
    <div
      className="d-flex align-items-center p-3 mb-3 bg-white"
      style={{ ...cardStyle, borderLeft: `4px solid ${color}` }}
    >
      <span
        className="d-flex align-items-center justify-content-center text-white mr-3"
        style={{ width: 44, height: 44, borderRadius: 8, backgroundColor: color }}
      >
        <i className={icon}></i>
      </span>
      <div>
        <small
          className="text-muted text-uppercase d-block"
          style={{ fontWeight: 600, letterSpacing: "0.03em" }}
        >
          {label}
        </small>
        <span style={{ fontSize: "1.4rem", fontWeight: 700 }}>{value}</span>
      </div>
    </div>
  </div>
);

const AddButton = ({ onClick, children, outline }) => (
  <button
    className={`btn ${outline ? "btn-outline-dark" : "btn-dark"} mt-2 mt-sm-0 ml-sm-2 d-inline-flex align-items-center`}
    style={{ borderRadius: 8, padding: "8px 18px 8px 10px", fontWeight: 500 }}
    onClick={onClick}
  >
    <span
      className="d-inline-flex align-items-center justify-content-center mr-2"
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        backgroundColor: outline ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.18)",
      }}
    >
      <i className="fas fa-plus" style={{ fontSize: 12 }} />
    </span>
    {children}
  </button>
);

const AllDevis = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(ALL_CLIENTS);
  const [dateBeginRaw, setDateBeginRaw] = useState("");
  const [dateEndRaw, setDateEndRaw] = useState("");
  const [inConfirmedDevis, setInConfirmedDevis] = useState([]);
  const [archivedDevis, setArchivedDevis] = useState([]);
  const [chargedInfo, setChargedInfo] = useState(false);
  const [alert, setAlert] = useState({ open: false, text: "" });

  const showError = (text) => setAlert({ open: true, text });
  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert((a) => ({ ...a, open: false }));
  };

  const canCreate = CREATE_ROLES.some((r) => userRole.includes(r));

  const loadLastMonth = () => {
    startLoading();
    SellingService.getDevisSellOfLastMonth(getCurrentUserId(), 0)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setInConfirmedDevis(response.data.success);
        }
        setChargedInfo(true);
      })
      .catch(() => showError("Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());

    startLoading();
    SellingService.getDevisSellOfLastMonth(getCurrentUserId(), 1)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setArchivedDevis(response.data.success);
        }
        setChargedInfo(true);
      })
      .catch(() => showError("Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());
  };

  useEffect(() => {
    const status = localStorage.getItem("status");
    setUserRole(status == null ? ["notConnected"] : JSON.parse(status));

    Societe.getSocietesClients("CLIENT", "CLIENT", 1, 0)
      .then((reponse) => setClients(reponse.data["Societe"] || []))
      .catch(() => showError("Erreur : il y a un problème pour obtenir les clients"));

    loadLastMonth();
  }, []);

  const clientOptions = useMemo(
    () => [
      ALL_CLIENTS,
      ...clients
        .filter((c) => c?.id !== undefined)
        .map((c) => ({ value: c.id, label: c.title })),
    ],
    [clients]
  );

  // Backend expects "any" when no date, and a full timestamp otherwise.
  const dateBegin = dateBeginRaw ? `${dateBeginRaw}T00:00:00` : "any";
  const dateEnd = dateEndRaw ? `${dateEndRaw}T23:59:59` : "any";

  const onGetDevisByFiltersHandler = () => {
    if (dateBeginRaw && dateEndRaw && dateBeginRaw > dateEndRaw) {
      showError("La date de début doit être antérieure à la date de fin");
      return;
    }
    startLoading();
    let pending = 2;
    const done = () => {
      pending -= 1;
      if (pending === 0) stopLoading();
    };
    [-1, 0].forEach((state) => {
      SellingService.getDevisByFilters(
        getCurrentUserId(),
        selectedClient.value,
        state,
        dateBegin,
        dateEnd
      )
        .then((response) => {
          if (response.data.success) {
            if (state === -1) setArchivedDevis(response.data.success);
            else setInConfirmedDevis(response.data.success);
          }
        })
        .catch(() => showError("Erreur : il y a un problème de connexion"))
        .finally(done);
    });
  };

  const onResetFilters = () => {
    setSelectedClient(ALL_CLIENTS);
    setDateBeginRaw("");
    setDateEndRaw("");
    loadLastMonth();
  };

  const caHT = inConfirmedDevis.reduce((s, r) => s + (Number(r.buyingPrice) || 0), 0);
  const caTT = inConfirmedDevis.reduce((s, r) => s + (Number(r.ttPrice) || 0), 0);
  const nbNonValide = inConfirmedDevis.filter((r) => r.state === 0).length;
  const nbCommande = inConfirmedDevis.filter((r) => r.state === 1).length;
  const nbBDC = inConfirmedDevis.filter((r) => r.state === 3).length;

  const kpisCount = [
    { label: "Non validés", value: nbNonValide, icon: "fas fa-hourglass-half", color: "#f0ad4e" },
    { label: "Liés à une commande", value: nbCommande, icon: "fas fa-file-signature", color: "#17a2b8" },
    { label: "Liés à un BDC", value: nbBDC, icon: "fas fa-check-circle", color: "#28a745" },
    { label: "Archivés", value: archivedDevis.length, icon: "fas fa-archive", color: "#6c757d" },
  ];
  const kpisCA = [
    { label: "CA H.T", value: fmt(caHT), icon: "fas fa-dollar-sign", color: "#343a40" },
    { label: "CA T.T", value: fmt(caTT), icon: "fas fa-dollar-sign", color: "#007bff" },
    { label: "Total TVA", value: fmt(caTT - caHT), icon: "fas fa-hand-holding-usd", color: "#dc3545" },
  ];

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-file-alt mr-2"></i>Les devis de vente
              </h4>
              <small className="text-muted">
                Par défaut, les devis ajoutés durant le dernier mois
              </small>
            </div>
            {canCreate && (
              <div className="d-flex flex-wrap">
                <AddButton onClick={() => navigate(`/addDevisPrice`)} outline>
                  Devis avec dépôt de prix
                </AddButton>
                <AddButton onClick={() => navigate(`/addDevis`)}>Nouveau devis</AddButton>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {chargedInfo && (
            <>
              <div className="row mt-3">
                {kpisCount.map((k) => (
                  <KpiCard key={k.label} {...k} />
                ))}
              </div>
              <div className="row">
                {kpisCA.map((k) => (
                  <KpiCard key={k.label} cols="col-md-4" {...k} />
                ))}
              </div>
            </>
          )}

          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>Filtres
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label style={labelStyle}>Client</label>
                    <Select
                      closeMenuOnSelect={true}
                      components={animatedComponents}
                      value={selectedClient}
                      onChange={setSelectedClient}
                      options={clientOptions}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="devisDateBegin" style={labelStyle}>
                      Date début
                    </label>
                    <input
                      id="devisDateBegin"
                      type="date"
                      className="form-control"
                      value={dateBeginRaw}
                      max={dateEndRaw || undefined}
                      onChange={(e) => setDateBeginRaw(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="devisDateEnd" style={labelStyle}>
                      Date fin
                    </label>
                    <input
                      id="devisDateEnd"
                      type="date"
                      className="form-control"
                      value={dateEndRaw}
                      min={dateBeginRaw || undefined}
                      onChange={(e) => setDateEndRaw(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer text-right">
              <button className="btn btn-outline-dark mr-2" onClick={onResetFilters}>
                <i className="fas fa-undo mr-1"></i> Réinitialiser
              </button>
              <button className="btn btn-dark" onClick={onGetDevisByFiltersHandler}>
                <i className="fas fa-search mr-1"></i> Valider le choix
              </button>
            </div>
          </div>

          {chargedInfo && (
            <>
              <DevisNonConfirme listDevis={inConfirmedDevis} userRole={userRole} />
              <DevisArchived listDevis={archivedDevis} />
            </>
          )}
        </div>
      </section>

      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={handleAlertClose} severity="error">
          {alert.text}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default withLoader(AllDevis);
